import type { CSSProperties, ReactNode } from "react";
import { useState } from "react";
import type { SelectedPerp, SelectedPred } from "../../types";
import { GoldSweep } from "./gold-sweep";

const PERP_COLOR = "#a78bfa";
const PRED_COLOR = "#60a5fa";
const BRAND = "#9ecbff";
const GOLD = "#f5a623";
const HL_MINT = "#97FCE4";

const HEDGE = "#60a5fa";
const AMPLIFY = "#a78bfa";
const HARVEST = "#f5a623";

const mono10: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  fontWeight: 600,
};

// ---------------------------------------------------------------------------
// Formula container — the gold-accented hero block showing the equation.
// ---------------------------------------------------------------------------

function FormulaChip({
  children,
  size = "md",
}: {
  children: ReactNode;
  size?: "md" | "lg";
}) {
  const isLg = size === "lg";
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: isLg ? 14 : 12,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        fontWeight: 700,
        color: "var(--fg-max)",
        padding: isLg ? "11px 18px" : "9px 16px",
        borderRadius: 4,
        border: "1px solid var(--line-3)",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.015) 100%)",
        boxShadow: "inset 0 0 22px rgba(255,255,255,0.04)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function FormulaOp({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 22,
        color: "var(--fg-meta)",
        fontWeight: 300,
        margin: "0 4px",
      }}
    >
      {children}
    </span>
  );
}

function FormulaCard() {
  return (
    <div
      style={{
        position: "relative",
        padding: "20px 28px",
        borderRadius: 6,
        border: "1px solid var(--line-2)",
        background: `
          linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(0,0,0,0.18) 100%),
          var(--bg-1)
        `,
        boxShadow: `
          inset 0 1px 0 rgba(255,255,255,0.04),
          0 1px 0 rgba(0,0,0,0.5)
        `,
        overflow: "hidden",
      }}
    >
      {/* Corner ticks — neutral grayscale */}
      <CornerTick position="top-left" />
      <CornerTick position="top-right" />
      <CornerTick position="bottom-left" />
      <CornerTick position="bottom-right" />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <FormulaChip>1× Perp</FormulaChip>
        <FormulaOp>×</FormulaOp>
        <FormulaChip>1× Prediction</FormulaChip>
        <FormulaOp>=</FormulaOp>
        <FormulaChip size="lg">Composite</FormulaChip>
      </div>
    </div>
  );
}

function CornerTick({
  position,
}: {
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}) {
  const size = 12;
  const stroke = "rgba(255,255,255,0.18)";
  const off = 8;
  const styles: CSSProperties = {
    position: "absolute",
    width: size,
    height: size,
    pointerEvents: "none",
  };
  if (position === "top-left") {
    styles.top = off;
    styles.left = off;
  } else if (position === "top-right") {
    styles.top = off;
    styles.right = off;
  } else if (position === "bottom-left") {
    styles.bottom = off;
    styles.left = off;
  } else {
    styles.bottom = off;
    styles.right = off;
  }
  // Two thin lines forming an L, rotated by position.
  const isTop = position.startsWith("top");
  const isLeft = position.endsWith("left");
  return (
    <div style={styles} aria-hidden>
      <span
        style={{
          position: "absolute",
          width: size,
          height: 1,
          background: stroke,
          top: isTop ? 0 : "auto",
          bottom: isTop ? "auto" : 0,
          left: isLeft ? 0 : "auto",
          right: isLeft ? "auto" : 0,
        }}
      />
      <span
        style={{
          position: "absolute",
          width: 1,
          height: size,
          background: stroke,
          top: isTop ? 0 : "auto",
          bottom: isTop ? "auto" : 0,
          left: isLeft ? 0 : "auto",
          right: isLeft ? "auto" : 0,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Use-case data
// ---------------------------------------------------------------------------

const USE_CASES: ReadonlyArray<{
  label: string;
  color: string;
  short: string;
  full: string;
  example: string;
}> = [
  {
    label: "Hedge",
    color: HEDGE,
    short: "Cap tail risk on a directional perp",
    full: "Use the prediction market to insure your perp position. If the perp moves against you, the prediction payout offsets the loss, so your downside is bounded.",
    example:
      "Long BTC perp + buy YES on \u201CBTC < $60K.\u201D If BTC dumps the perp bleeds but the prediction pays, turning a directional bet into a capped one.",
  },
  {
    label: "Amplify",
    color: AMPLIFY,
    short: "Stack a same-direction prediction onto a catalyst",
    full: "When you have conviction around a specific event or threshold, layer a same-direction prediction on top of the perp. Both legs win together if you're right.",
    example:
      "Long BTC perp + buy YES on \u201CBTC \u2265 $90K by month-end.\u201D If the rally lands above the threshold, perp PnL stacks on the prediction payout.",
  },
  {
    label: "Harvest",
    color: HARVEST,
    short: "Monetise the high-probability side of a market",
    full: "Find a prediction trading near the extremes (e.g. 92¢/8¢) and harvest the cheap side. Hedge the residual tail risk with a small perp position to lock in near-riskless yield.",
    example:
      "Buy NO on \u201CBTC > $200K by Friday\u201D at 4¢. Take 96¢ of upside if it doesn\u2019t happen, with tiny tail risk neutralised by a small long perp leg.",
  },
];

// ---------------------------------------------------------------------------
// Use-case showcase — 1 expanded card + 2 stacked, auto-cycles every few sec.
// Hover anywhere in the showcase pauses the cycle. Clicking a stacked card
// promotes it to the expanded slot.
// ---------------------------------------------------------------------------

const CYCLE_MS = 5000;

// Mini payoff-curve SVG used inside the use-case cards. Each shape is a
// schematic of the structured product's expected payoff profile so the user
// gets the geometry of the trade at a glance.
function MiniPayoff({
  shape,
  color,
  height = 64,
}: {
  shape: "hedge" | "amplify" | "harvest";
  color: string;
  height?: number;
}) {
  const lines: Record<typeof shape, string> = {
    // Capped downside, free upside — kink at threshold.
    hedge: "M2,52 L34,52 L52,30 L98,5",
    // Slow rise, then steeper rally past the threshold.
    amplify: "M2,44 Q30,42 42,34 Q60,18 98,4",
    // Flat high payoff for most outcomes, sharp drop at the unlikely tail.
    harvest: "M2,12 L66,12 L96,54",
  };
  const fills: Record<typeof shape, string> = {
    hedge: "M2,52 L34,52 L52,30 L98,5 L98,60 L2,60 Z",
    amplify: "M2,44 Q30,42 42,34 Q60,18 98,4 L98,60 L2,60 Z",
    harvest: "M2,12 L66,12 L96,54 L98,60 L2,60 Z",
  };
  const gradId = `mp-${shape}`;
  return (
    <svg
      viewBox="0 0 100 60"
      preserveAspectRatio="none"
      width="100%"
      height={height}
      aria-hidden
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.42" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* zero baseline */}
      <line
        x1="0"
        y1="50"
        x2="100"
        y2="50"
        stroke={`${color}33`}
        strokeWidth="0.5"
        strokeDasharray="2 2"
      />
      {/* threshold marker */}
      <line
        x1="50"
        y1="0"
        x2="50"
        y2="60"
        stroke={`${color}3a`}
        strokeWidth="0.5"
        strokeDasharray="3 3"
      />
      {/* under-curve fill */}
      <path d={fills[shape]} fill={`url(#${gradId})`} />
      {/* curve */}
      <path
        d={lines[shape]}
        stroke={color}
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function shapeFor(label: string): "hedge" | "amplify" | "harvest" {
  return label.toLowerCase() as "hedge" | "amplify" | "harvest";
}

function ExpandedCard({
  uc,
  paused,
  cycleMs,
  onCycleComplete,
}: {
  uc: (typeof USE_CASES)[number];
  paused: boolean;
  cycleMs: number;
  onCycleComplete: () => void;
}) {
  const { label, color, full, example } = uc;
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: "22px 24px 24px",
        borderRadius: 4,
        border: "1px solid var(--line-2)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.018) 0%, transparent 100%), var(--bg-1)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Animated colored border that fills around the perimeter as the
          cycle ticks down. Doubles as the timer indicator. */}
      <TimerBorder
        color={color}
        cycleMs={cycleMs}
        paused={paused}
        onComplete={onCycleComplete}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 12px ${color}, 0 0 4px ${color}`,
          }}
        />
        <span
          style={{
            ...mono10,
            fontSize: 12,
            color,
            letterSpacing: "0.26em",
            fontWeight: 700,
          }}
        >
          {label}
        </span>
        <span
          style={{
            ...mono10,
            fontSize: 8,
            color: "var(--fg-dim)",
            marginLeft: "auto",
            letterSpacing: "0.18em",
          }}
        >
          PAYOFF SHAPE
        </span>
      </div>
      <div
        style={{
          padding: "6px 10px",
          borderRadius: 3,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, transparent 100%)",
          border: "1px solid var(--line-1)",
          position: "relative",
        }}
      >
        <MiniPayoff shape={shapeFor(label)} color={color} height={64} />
      </div>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--f-sans)",
          fontSize: 14,
          lineHeight: 1.45,
          color: "var(--fg-body)",
          letterSpacing: "-0.005em",
          position: "relative",
        }}
      >
        {full}
      </p>
      <div
        style={{
          marginTop: "auto",
          padding: "10px 12px",
          borderRadius: 3,
          background: "rgba(0,0,0,0.32)",
          border: "1px solid var(--line-1)",
          position: "relative",
        }}
      >
        <span style={{ ...mono10, fontSize: 9, color: "var(--fg-dim)" }}>
          EXAMPLE
        </span>
        <div
          style={{
            marginTop: 4,
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--fg-meta)",
          }}
        >
          {example}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimerBorder — colored stroke that traces around the rectangle perimeter
// over the cycle duration. Pauses when `paused`. Fires `onComplete` at the
// end so the parent advances the cycle from this single source of truth.
// ---------------------------------------------------------------------------

function TimerBorder({
  color,
  cycleMs,
  paused,
  onComplete,
}: {
  color: string;
  cycleMs: number;
  paused: boolean;
  onComplete: () => void;
}) {
  return (
    <svg
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      <rect
        x="1.25"
        y="1.25"
        width="calc(100% - 2.5px)"
        height="calc(100% - 2.5px)"
        rx="3"
        ry="3"
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        pathLength={100}
        strokeDasharray={100}
        strokeLinecap="round"
        style={{
          filter: `drop-shadow(0 0 6px ${color}66)`,
          animation: `hh-timer-fill ${cycleMs}ms linear forwards`,
          animationPlayState: paused ? "paused" : "running",
        }}
        onAnimationEnd={onComplete}
      />
    </svg>
  );
}

function CollapsedCard({
  uc,
  onPromote,
}: {
  uc: (typeof USE_CASES)[number];
  onPromote: () => void;
}) {
  const { label, color, short } = uc;
  return (
    <button
      type="button"
      onClick={onPromote}
      aria-label={`Show ${label} details`}
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "1fr 56px",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        textAlign: "left",
        borderRadius: 4,
        border: "1px solid var(--line-2)",
        background: "var(--bg-1)",
        cursor: "pointer",
        transition: "background 220ms ease, border-color 220ms ease",
        height: "100%",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background =
          "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, var(--bg-1) 100%)";
        e.currentTarget.style.borderColor = "var(--line-3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--bg-1)";
        e.currentTarget.style.borderColor = "var(--line-2)";
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <span
          style={{
            ...mono10,
            fontSize: 10,
            color,
            letterSpacing: "0.22em",
            fontWeight: 700,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontFamily: "var(--f-sans)",
            fontSize: 12,
            color: "var(--fg-body)",
            lineHeight: 1.35,
          }}
        >
          {short}
        </span>
      </div>
      <div
        aria-hidden
        style={{
          width: 56,
          height: 36,
          opacity: 0.95,
        }}
      >
        <MiniPayoff shape={shapeFor(label)} color={color} height={36} />
      </div>
    </button>
  );
}

function UseCaseShowcase() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const stacked = USE_CASES.map((uc, i) => ({ uc, i })).filter(
    ({ i }) => i !== activeIdx,
  );

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        width: "100%",
        height: 240,
        display: "grid",
        gridTemplateColumns: "1.6fr 1fr",
        gap: 12,
      }}
    >
      <ExpandedCard
        // remount each cycle so the timer animation runs fresh
        key={activeIdx}
        uc={USE_CASES[activeIdx]!}
        paused={paused}
        cycleMs={CYCLE_MS}
        onCycleComplete={() =>
          setActiveIdx((i) => (i + 1) % USE_CASES.length)
        }
      />
      <div
        style={{
          display: "grid",
          gridTemplateRows: "1fr 1fr",
          gap: 12,
          minHeight: 0,
        }}
      >
        {stacked.map(({ uc, i }) => (
          <CollapsedCard
            key={uc.label}
            uc={uc}
            onPromote={() => setActiveIdx(i)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selection cards (inline, replaces the explainer once user clicks the CTA)
// ---------------------------------------------------------------------------

function hueFor(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}
function predTint(seed: string): { tint: string; gradient: string } {
  const hue = hueFor(seed);
  const tint = `hsla(${hue}, 72%, 58%, 0.25)`;
  const gradient = `
    radial-gradient(120% 120% at 0% 0%, hsla(${hue}, 72%, 58%, 0.22), transparent 55%),
    linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
  `;
  return { tint, gradient };
}
function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 100 ? 0 : 2,
  });
}

function PerpSelectCard({
  market,
  onClick,
}: {
  market: SelectedPerp | null;
  onClick: () => void;
}) {
  if (!market) {
    return (
      <SelectShell color={PERP_COLOR} state="empty" onClick={onClick}>
        <SelectHeader badge="STEP 1 · PERP" color={PERP_COLOR}>
          <img
            src="/hyperliquid/wordmark/wordmark-white.svg"
            alt="Hyperliquid"
            style={{ height: 12, opacity: 0.55 }}
          />
        </SelectHeader>
        <EmptyBody color={PERP_COLOR} title="Choose your perp">
          Pick any Hyperliquid market for the linear directional leg.
        </EmptyBody>
      </SelectShell>
    );
  }
  const chgColor = market.dayChangePct >= 0 ? "var(--up)" : "var(--dn)";
  return (
    <SelectShell color={PERP_COLOR} state="selected" onClick={onClick}>
      <SelectHeader badge="PERP · HYPERLIQUID" color={PERP_COLOR}>
        <ChangeChip color={PERP_COLOR} />
      </SelectHeader>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/hyperliquid/icon/icon-mintgreen.svg"
            alt=""
            style={{ width: 22, height: 22 }}
          />
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 22,
              fontWeight: 600,
              color: "var(--fg-max)",
              letterSpacing: "0.02em",
            }}
          >
            {market.coin}
          </span>
          <span
            style={{
              ...mono10,
              fontSize: 9,
              color: HL_MINT,
              border: `1px solid ${HL_MINT}55`,
              background: `${HL_MINT}12`,
              padding: "3px 7px",
              borderRadius: 2,
            }}
          >
            {market.maxLeverage}× MAX
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            paddingTop: 4,
            borderTop: "1px solid var(--line-1)",
          }}
        >
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 24,
              color: "var(--fg-max)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 500,
            }}
          >
            {formatPrice(market.markPx)}
          </span>
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 13,
              color: chgColor,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {market.dayChangePct >= 0 ? "+" : ""}
            {market.dayChangePct.toFixed(2)}%
          </span>
        </div>
      </div>
    </SelectShell>
  );
}

function PredSelectCard({
  market,
  onClick,
}: {
  market: SelectedPred | null;
  onClick: () => void;
}) {
  if (!market) {
    return (
      <SelectShell color={PRED_COLOR} state="empty" onClick={onClick}>
        <SelectHeader badge="STEP 2 · PREDICTION" color={PRED_COLOR}>
          <img
            src="/polymarket/logos/logo-white.svg"
            alt="Polymarket"
            style={{ height: 12, opacity: 0.55 }}
          />
        </SelectHeader>
        <EmptyBody color={PRED_COLOR} title="Choose your prediction">
          Pick any Polymarket question for the event-driven kink.
        </EmptyBody>
      </SelectShell>
    );
  }
  const seed = market.category ?? market.id ?? market.question;
  const { tint, gradient } = predTint(seed);
  const yesPct =
    market.yesPrice !== undefined ? (market.yesPrice * 100).toFixed(1) : "—";
  const noPct =
    market.noPrice !== undefined ? (market.noPrice * 100).toFixed(1) : "—";
  return (
    <SelectShell color={PRED_COLOR} state="selected" onClick={onClick}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: gradient,
          opacity: 0.7,
          borderRadius: 4,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <SelectHeader badge="PREDICTION · POLYMARKET" color={PRED_COLOR}>
          <ChangeChip color={PRED_COLOR} />
        </SelectHeader>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {market.image ? (
            <img
              src={market.image}
              alt=""
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                objectFit: "cover",
                border: `1px solid ${tint}`,
                borderRadius: 3,
                boxShadow: `0 0 0 2px rgba(0,0,0,0.2)`,
              }}
            />
          ) : (
            <img
              src="/polymarket/logos/icon-blue.svg"
              alt=""
              style={{ width: 36, height: 36, flexShrink: 0, opacity: 0.9 }}
            />
          )}
          <div
            style={{
              fontFamily: "var(--f-sans)",
              fontSize: 14,
              color: "var(--fg-max)",
              lineHeight: 1.35,
              minWidth: 0,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              flex: 1,
            }}
            title={market.question}
          >
            {market.question}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 14,
            paddingTop: 4,
            borderTop: "1px solid var(--line-1)",
          }}
        >
          <span style={{ ...mono10, fontSize: 8, color: "var(--fg-dim)" }}>
            YES / NO
          </span>
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 18,
              color: "var(--up)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {yesPct}¢
          </span>
          <span style={{ color: "var(--fg-dim)", fontSize: 12 }}>/</span>
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 18,
              color: "var(--dn)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {noPct}¢
          </span>
        </div>
      </div>
    </SelectShell>
  );
}

function SelectShell({
  color,
  state,
  onClick,
  children,
}: {
  color: string;
  state: "empty" | "selected";
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: "20px 22px",
        textAlign: "left",
        borderRadius: 4,
        border:
          state === "empty"
            ? `1px dashed ${color}77`
            : `1px solid ${color}55`,
        background:
          state === "empty"
            ? `linear-gradient(180deg, ${color}0c 0%, transparent 100%)`
            : "var(--bg-1)",
        boxShadow:
          state === "empty"
            ? `inset 2px 0 0 ${color}aa`
            : `inset 2px 0 0 ${color}, 0 0 24px ${color}1a`,
        cursor: "pointer",
        minHeight: 220,
        transition:
          "background 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={(e) => {
        if (state !== "empty") return;
        e.currentTarget.style.background = `linear-gradient(180deg, ${color}1c 0%, transparent 100%)`;
      }}
      onMouseLeave={(e) => {
        if (state !== "empty") return;
        e.currentTarget.style.background = `linear-gradient(180deg, ${color}0c 0%, transparent 100%)`;
      }}
    >
      {children}
    </button>
  );
}

function SelectHeader({
  badge,
  color,
  children,
}: {
  badge: string;
  color: string;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span
        style={{
          ...mono10,
          fontSize: 9,
          color,
          letterSpacing: "0.24em",
          fontWeight: 700,
        }}
      >
        {badge}
      </span>
      {children}
    </div>
  );
}

function EmptyBody({
  color,
  title,
  children,
}: {
  color: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        textAlign: "center",
        padding: "8px 0",
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 22,
          fontWeight: 500,
          color: "var(--fg-max)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 13,
          color: "var(--fg-meta)",
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        {children}
      </div>
      <span
        style={{
          ...mono10,
          fontSize: 10,
          color,
          padding: "8px 14px",
          border: `1px solid ${color}88`,
          borderRadius: 2,
          marginTop: 4,
        }}
      >
        BROWSE MARKETS →
      </span>
    </div>
  );
}

function ChangeChip({ color }: { color: string }) {
  return (
    <span
      style={{
        ...mono10,
        fontSize: 9,
        color: `${color}cc`,
        padding: "4px 8px",
        borderRadius: 2,
        border: `1px solid ${color}55`,
        background: `${color}10`,
      }}
    >
      Change ↻
    </span>
  );
}

// ---------------------------------------------------------------------------
// DesktopLanding (with inline selection phase)
// ---------------------------------------------------------------------------

export function DesktopLanding({
  selectedPerp,
  selectedPred,
  onPickPerp,
  onPickPred,
  onCompose,
  revealingTrading = false,
  initialPhase = "explain",
}: {
  selectedPerp: SelectedPerp | null;
  selectedPred: SelectedPred | null;
  onPickPerp: () => void;
  onPickPred: () => void;
  onCompose: () => void;
  /** Set true by parent while the select → trading reveal is playing. */
  revealingTrading?: boolean;
  initialPhase?: "explain" | "select";
}) {
  const [phase, setPhase] = useState<"explain" | "select">(initialPhase);
  const [revealing, setRevealing] = useState(false);
  const ready = !!selectedPerp && !!selectedPred;

  const triggerReveal = () => {
    if (phase === "select" || revealing) return;
    setRevealing(true);
    // mid-transition: swap content while gold sweep is at centre
    window.setTimeout(() => setPhase("select"), 320);
    // end of full transition: clear flag (animations are forwards so state stays)
    window.setTimeout(() => setRevealing(false), 1000);
  };

  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px 64px",
        background: `
          radial-gradient(ellipse 70% 45% at 50% 0%, ${GOLD}08 0%, transparent 60%),
          radial-gradient(ellipse 50% 35% at 50% 100%, ${BRAND}07 0%, transparent 60%),
          radial-gradient(ellipse 30% 60% at 0% 50%, ${PERP_COLOR}05 0%, transparent 70%),
          radial-gradient(ellipse 30% 60% at 100% 50%, ${PRED_COLOR}05 0%, transparent 70%)
        `,
      }}
    >
      {/* Faint dot grid for terminal vibe */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0)",
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(ellipse 70% 60% at 50% 50%, #000 0%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at 50% 50%, #000 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      {revealing && <GoldSweep />}
      <div
        style={{
          width: "100%",
          maxWidth: 980,
          display: "flex",
          flexDirection: "column",
          gap: 24,
          alignItems: "center",
          // When parent (app.tsx) is revealing the trading workspace, fade
          // the whole landing out together — the parent paints the gold
          // sweep over the top.
          animation: revealingTrading
            ? "hh-reveal-out 380ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
            : undefined,
        }}
      >
        {/* Hero (always visible — short when in select phase) */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            textAlign: "center",
          }}
        >
          <span
            style={{
              ...mono10,
              fontSize: 10,
              color: "var(--fg-dim)",
              letterSpacing: "0.32em",
              animation:
                "hh-stagger-in 540ms cubic-bezier(0.16, 1, 0.3, 1) backwards",
            }}
          >
            COMPOSITE DERIVATIVES
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--f-sans)",
              fontSize:
                phase === "explain" ? "clamp(40px, 5vw, 60px)" : 28,
              lineHeight: 1.02,
              fontWeight: 500,
              color: "var(--fg-max)",
              letterSpacing: "-0.024em",
              transition: "font-size 320ms ease",
              maxWidth: 880,
              backgroundImage:
                phase === "explain"
                  ? `linear-gradient(180deg, var(--fg-max) 0%, ${BRAND}cc 110%)`
                  : "none",
              WebkitBackgroundClip: phase === "explain" ? "text" : undefined,
              backgroundClip: phase === "explain" ? "text" : undefined,
              WebkitTextFillColor:
                phase === "explain" ? "transparent" : undefined,
              animation:
                "hh-stagger-in 580ms cubic-bezier(0.16, 1, 0.3, 1) 80ms backwards",
            }}
          >
            {phase === "explain" ? "Welcome to Composites." : "Pick your two legs."}
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--f-sans)",
              fontSize: phase === "explain" ? 16 : 13,
              lineHeight: 1.5,
              color: phase === "explain" ? "var(--fg-body)" : "var(--fg-meta)",
              maxWidth: 660,
              transition: "font-size 320ms ease, color 320ms ease",
              animation:
                "hh-stagger-in 540ms cubic-bezier(0.16, 1, 0.3, 1) 160ms backwards",
            }}
          >
            {phase === "explain"
              ? "Structured products that combine one perp position with one prediction market position. Each combination shapes a unique payoff curve you cannot buy on any single venue."
              : "One perp, one prediction. We will compose them into a single payoff curve."}
          </p>
        </div>

        {/* Explainer block — animates out when revealing */}
        {phase === "explain" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              width: "100%",
              alignItems: "center",
              animation: revealing
                ? "hh-reveal-out 320ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : undefined,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 720,
                animation:
                  "hh-stagger-in 580ms cubic-bezier(0.16, 1, 0.3, 1) 260ms backwards",
              }}
            >
              <FormulaCard />
            </div>
            <div
              style={{
                width: "100%",
                animation:
                  "hh-stagger-in 580ms cubic-bezier(0.16, 1, 0.3, 1) 360ms backwards",
              }}
            >
              <UseCaseShowcase />
            </div>
          </div>
        )}

        {/* Selection block — animates in on enter, animates out when parent
            triggers the trading reveal */}
        {phase === "select" && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              animation: revealingTrading
                ? "hh-reveal-out 320ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : revealing
                  ? "hh-reveal-in 580ms cubic-bezier(0.16, 1, 0.3, 1) forwards"
                  : undefined,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: 14,
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  animation: revealing
                    ? "hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 80ms backwards"
                    : undefined,
                }}
              >
                <PerpSelectCard market={selectedPerp} onClick={onPickPerp} />
              </div>
              <div
                aria-hidden
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  flexShrink: 0,
                  animation: revealing
                    ? "hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 160ms backwards"
                    : undefined,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 22,
                    color: GOLD,
                    fontWeight: 300,
                    textShadow: `0 0 12px ${GOLD}66`,
                  }}
                >
                  ×
                </span>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  animation: revealing
                    ? "hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 240ms backwards"
                    : undefined,
                }}
              >
                <PredSelectCard market={selectedPred} onClick={onPickPred} />
              </div>
            </div>
          </div>
        )}

        {/* CTA — toggles phase or composes */}
        {phase === "explain" ? (
          <button
            type="button"
            onClick={triggerReveal}
            style={{
              ...primaryCta(true),
              animation: revealing
                ? "hh-reveal-out 240ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : "hh-stagger-in 540ms cubic-bezier(0.16, 1, 0.3, 1) 480ms backwards",
            }}
            onMouseEnter={(e) => !revealing && primaryCtaHover(e.currentTarget, true)}
            onMouseLeave={(e) => !revealing && primaryCtaHover(e.currentTarget, false)}
          >
            Try it for yourself →
          </button>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              animation: revealingTrading
                ? "hh-reveal-out 320ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : revealing
                  ? "hh-stagger-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 380ms backwards"
                  : undefined,
            }}
          >
            <button
              type="button"
              disabled={!ready || revealingTrading}
              onClick={ready && !revealingTrading ? onCompose : undefined}
              style={primaryCta(ready)}
              onMouseEnter={(e) => ready && !revealingTrading && primaryCtaHover(e.currentTarget, true)}
              onMouseLeave={(e) => ready && !revealingTrading && primaryCtaHover(e.currentTarget, false)}
            >
              {ready ? "Reveal Composite →" : "Pick both legs to reveal"}
            </button>
            <button
              type="button"
              onClick={() => setPhase("explain")}
              style={{
                ...mono10,
                fontSize: 9,
                background: "transparent",
                border: "none",
                color: "var(--fg-dim)",
                cursor: "pointer",
                letterSpacing: "0.22em",
                padding: "4px 8px",
              }}
            >
              ← BACK TO OVERVIEW
            </button>
          </div>
        )}
        {phase === "explain" && (
          <span
            style={{
              ...mono10,
              fontSize: 9,
              color: "var(--fg-dim)",
              letterSpacing: "0.22em",
              marginTop: -6,
            }}
          >
            NO WALLET NEEDED TO SIMULATE
          </span>
        )}
      </div>
    </div>
  );
}

// GoldSweep is exported from ./gold-sweep — used both here and by app.tsx
// for the parent-driven select → trading transition.

// ---------------------------------------------------------------------------
// Primary CTA styling
// ---------------------------------------------------------------------------

function primaryCta(active: boolean): CSSProperties {
  return {
    padding: "14px 32px",
    fontFamily: "var(--f-mono)",
    fontSize: 12,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    fontWeight: 700,
    color: active ? "var(--bg-0)" : "var(--fg-dim)",
    background: active
      ? `linear-gradient(180deg, ${BRAND} 0%, #7ab3ee 100%)`
      : "transparent",
    border: active ? `1px solid ${BRAND}` : "1px solid var(--line-3)",
    borderRadius: 3,
    cursor: active ? "pointer" : "not-allowed",
    boxShadow: active
      ? `0 0 24px ${BRAND}40, inset 0 1px 0 rgba(255,255,255,0.3)`
      : "none",
    transition:
      "background 200ms ease, color 200ms ease, box-shadow 200ms ease, transform 140ms ease",
  };
}

function primaryCtaHover(el: HTMLButtonElement, hovered: boolean) {
  if (hovered) {
    el.style.transform = "translateY(-1px)";
    el.style.boxShadow = `0 4px 28px ${BRAND}55, inset 0 1px 0 rgba(255,255,255,0.35)`;
  } else {
    el.style.transform = "translateY(0)";
    el.style.boxShadow = `0 0 24px ${BRAND}40, inset 0 1px 0 rgba(255,255,255,0.3)`;
  }
}
